export const API_BASE_URL = "https://opsapis.xpansecoffee.com";

export class ApiError extends Error {
    constructor(message, status = 0, errors = null) {
        super(message || "Request failed");
        this.name = "ApiError";
        this.status = status;
        this.errors = errors;
    }
}

export function getToken() {
    return localStorage.getItem("token");
}

export function getStaff() {
    try {
        const raw = localStorage.getItem("staff");
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function saveSession({ token, staff }) {
    localStorage.setItem("token", token);
    localStorage.setItem("staff", JSON.stringify(staff || null));

    if (staff?.outlet) {
        localStorage.setItem("outlet", JSON.stringify(staff.outlet));
    }
}

export function clearSession() {
    localStorage.removeItem("token");
    localStorage.removeItem("staff");
    localStorage.removeItem("outlet");
}

export function flattenErrors(errors) {
    if (!errors || typeof errors !== "object") return [];

    return Object.values(errors).flatMap((value) => {
        if (Array.isArray(value)) {
            return value.flatMap((item) =>
                typeof item === "string" ? [item] : flattenErrors(item)
            );
        }

        if (value && typeof value === "object") {
            return flattenErrors(value);
        }

        if (value === undefined || value === null || value === "") return [];

        return [String(value)];
    });
}

async function apiRequest(path, { method = "GET", body, auth = true, query } = {}) {
    const url = new URL(path, `${API_BASE_URL}/`);

    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            if (value === undefined || value === null || value === "") return;
            url.searchParams.set(key, String(value));
        });
    }

    const headers = { Accept: "application/json" };

    if (body !== undefined) {
        headers["Content-Type"] = "application/json";
    }

    if (auth) {
        const token = getToken();

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
    }

    let response;

    try {
        response = await fetch(url.toString(), {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            cache: "no-store",
        });
    } catch {
        throw new ApiError(
            "Unable to reach the server. Check your connection and try again.",
            0
        );
    }

    let json = {};

    try {
        json = await response.json();
    } catch {
        json = {};
    }

    if (response.status === 401 && auth) {
        clearSession();

        if (window.location.pathname !== "/") {
            window.location.assign("/");
        }
    }

    if (!response.ok || json.success === false) {
        throw new ApiError(
            json.message || "Request failed",
            response.status,
            json.errors || null
        );
    }

    return json;
}

export const authApi = {
    login(mobileNumber) {
        return apiRequest("/api/auth/login", {
            method: "POST",
            auth: false,
            body: { mobile_number: mobileNumber },
        });
    },

    me() {
        return apiRequest("/api/auth/me");
    },
};

export const checklistApi = {
    available() {
        return apiRequest("/api/checklists/available");
    },

    form({ categoryId, checklistType, frequency, businessDate }) {
        return apiRequest("/api/checklists/form", {
            query: {
                category_id: categoryId,
                checklist_type: checklistType,
                frequency,
                business_date: businessDate,
            },
        });
    },

    createSubmission(body) {
        return apiRequest("/api/checklists/submissions", {
            method: "POST",
            body,
        });
    },

    updateSubmission(id, body) {
        return apiRequest(`/api/checklists/submissions/${id}`, {
            method: "PUT",
            body,
        });
    },

    listSubmissions(query) {
        return apiRequest("/api/checklists/submissions", { query });
    },

    getSubmission(id) {
        return apiRequest(`/api/checklists/submissions/${id}`);
    },
};

export function normalizeProfile(data) {
    if (!data || typeof data !== "object") return null;

    const staff =
        data.staff && typeof data.staff === "object" ? data.staff : data;

    return {
        ...staff,
        staff_name: staff.staff_name || staff.name || "",
        outlet: staff.outlet || data.outlet || null,
    };
}

export function unwrapSubmissionList(data) {
    if (Array.isArray(data)) {
        return { rows: data, pagination: null };
    }

    if (!data || typeof data !== "object") {
        return { rows: [], pagination: null };
    }

    const rows = [data.submissions, data.checklists, data.items, data.data].find(
        Array.isArray
    );

    return {
        rows: rows || [],
        pagination: data.pagination || data.meta || null,
    };
}
